// App.js
import React, { useEffect, useState } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import store from './store';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Analytics } from "@vercel/analytics/react"
import axios from 'axios';

import VTIPage from './Pages/VTIPage/VTIPage.jsx';
import LandingPage from './Pages/LandingPage/LandingPage.jsx';
import LoginPage from './Pages/Login/LoginPage';
import SignupPage from './Pages/SignupPage/SignupPage';
import MainPage from './Pages/Main/MainPage';
import ClubsPage from './Pages/ClubsPage/ClubsPage';
import ClubChatPage from './Pages/Clubs/ClubChatPage.jsx';
import LikesPage from './Pages/Likes/LikesPage';
import MyPage from './Pages/MyPage/MyPage';
import MyPageV2 from './Pages/MyPage/MyPageV2';
import CustomerService from './Pages/MyPage/CustomerService';
import Navbar from './Components/Navbar/Navbar';
import LoginSuccess from './Pages/Login/LoginSuccess';
import FindIdPage from './Pages/FindId/FindIdPage';
import FindPwPage from './Pages/FindPw/FindPwPage';
import ResetPasswordPage from './Pages/FindPw/ResetPasswordPage';
import MapPage from './Pages/MapPage/MapPage';
import EditInfo from './Pages/EditInfo/EditInfo';
import NotificationSettings from './Pages/NotificationSettings/NotificationSettings';
import PhoneVerificationPage from './Pages/Phone/PhoneVerificationPage';
import TicketPurchasePage from './Pages/TicketPurchase/TicketPurchasePage';
import DeepLinkListener from './Components/DeepLinkListener';
import TicketInfoPage from './Pages/TicketInfo/TicketInfoPage';
import PaymentSuccess from "./Pages/Payment/PaymentSuccess";
import PaymentFail from "./Pages/Payment/PaymentFail";
// MyPageV2는 이제 MyPage로 통합됨
import Announcements from './Pages/Announcements/Announcements';
import AdminAnnouncementPage from './Pages/AdminAnnouncement/AdminAnnouncementPage';
import TermsOfService from './Pages/TermsOfService/TermsOfService';
import PrivacyPolicyPage from './Pages/PrivacyPolicy/PrivacyPolicy';
import MarketingConsentPage from './Pages/MarketingConsent/MarketingConsentPage';
// TestClubCardV2는 이제 ClubsPage로 import됨
import EventCalendar from './Pages/EventCalendar/EventCalendar';
import EventDetailPage from './Pages/EventDetail/EventDetailPage';

// Owner 관련 페이지
import OwnerLoginPage from './Pages/Owner/OwnerLogin/OwnerLoginPage';
import OwnerRegisterPage from './Pages/Owner/OwnerRegister/OwnerRegisterPage';
import AdminLandingPage from './Pages/Owner/AdminLandingPage/AdminLandingPage';
import OwnerDashboardPage from './Pages/Owner/OwnerDashboardPage/OwnerDashboardPage.jsx';
import OwnerEditInfoPage from './Pages/Owner/OwnerEditInfoPage/OwnerEditInfoPage';
import ClubManagementPage from './Pages/Owner/ClubManagementPage/ClubManagementPage';
import EventManagementPage from './Pages/Owner/EventManagementPage/EventManagementPage';
import TicketSettingsPage from './Pages/Owner/TicketSettings/TicketSettingsPage';
import BannerManagementPage from './Pages/Owner/BannerManagementPage/BannerManagementPage';

import BannerAdmin from './Pages/BannerAdmin/BannerAdmin.jsx';

import { loginWithToken, restoreAuthState } from "./store/slices/authSlice";
import { fetchAllEvents } from "./store/slices/eventsSlice";
import { registerPushNotifications } from "./services/pushNotifications";
import LoadingSpinner from './Components/LoadingSpinner/LoadingSpinner';

// ─────────────────────────────────────────────────────
// 1) 인증 보호용 라우트
// ─────────────────────────────────────────────────────
const ProtectedRoute = ({ isAuthenticated, isLoading }) => {
  if (isLoading) return <LoadingSpinner />;
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
};

// ─────────────────────────────────────────────────────
// 2) 특정 주소에서만 Navbar 숨기기
// ─────────────────────────────────────────────────────
const NavbarWrapper = () => {
  const location = useLocation();
  const hiddenRoutes = [
    '/login',
    '/signup',
    '/find-id',
    '/find-pw',
    '/account-info',
    '/map',
    '/profile-management',
    '/edit-info',
    '/notification-settings',
  ];
  return !hiddenRoutes.includes(location.pathname) ? <Navbar /> : null;
};

// 오너용 보호된 라우트 컴포넌트 추가
const OwnerProtectedRoute = () => {
  const { isAuthenticated } = useSelector(state => state.ownerAuth);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/owner/login');
    }
  }, [isAuthenticated, navigate]);

  return isAuthenticated ? <Outlet /> : null;
};

// 로그인된 사용자를 위한 리다이렉트 컴포넌트
const OwnerAuthRedirect = () => {
  const { isAuthenticated } = useSelector(state => state.ownerAuth);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/owner/dashboard');
    }
  }, [isAuthenticated, navigate]);

  return null;
};

// ─────────────────────────────────────────────────────
// 3) 전체 앱 라우트
//    - 휴대폰 번호 없는 유저는 phone-verification 페이지로 강제 이동
// ─────────────────────────────────────────────────────
const AppRoutes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { token, isAuthenticated, isGuest, isLoading, user } = useSelector((state) => state.auth);
  const { allEvents } = useSelector((state) => state.events);

  // (A) 앱 시작 시 이벤트 데이터 불러오기 (Redux 사용)
  useEffect(() => {
    dispatch(fetchAllEvents());
  }, [dispatch]);

  // 백엔드 데이터를 프론트엔드 포맷에 맞게 변환
  const formattedEvents = allEvents.map(event => ({
    id: event.id,
    title: event.title,
    date: event.event_date,
    time: event.start_time && event.end_time 
      ? `${event.start_time} - ${event.end_time}`
      : 'N/A',
    start_time: event.start_time,
    end_time: event.end_time,
    location: event.club && event.club.name ? event.club.name : (event.location || '장소 미정'),
    description: event.description,
    genre: event.genre,
    poster_image_url: event.poster_image_url,
    instagram_url: event.instagram_url,
    like_count: event.like_count || 0,
    club: event.club, // 클럽 정보도 전달
  }));

  // (B) 앱 시작 시 토큰/유저 복원
  useEffect(() => {
    dispatch(restoreAuthState());
  }, [dispatch]);

  // (C) 토큰은 있지만 아직 로그인 안 된 상태면, 백엔드에 유효성 확인
  useEffect(() => {
    if (token && !isAuthenticated) {
      dispatch(loginWithToken(token));
    }
  }, [token, isAuthenticated, dispatch]);

  // (C) 로그인된 상태인데, phone이 없으면 무조건 /phone-verification으로 이동
  //     (단, 이미 phone-verification 페이지면 중복으로 이동하지 않도록 체크)
  useEffect(() => {
    if (isAuthenticated && !isGuest && user && !user.phone) {
      if (location.pathname !== '/phone-verification') {
        navigate('/phone-verification');
      }
    }
  }, [isAuthenticated, isGuest, user, location.pathname, navigate]);

  return (
    <div className="app-container">
      <Routes>
        {/* 로그인, 회원가입, 폰인증, 비밀번호 찾기 등 */}
        <Route
          path="/login"
          element={
            (isAuthenticated && !isGuest)
              ? <Navigate to="/home" />
              : <LoginPage />
          }
        />
        <Route
          path="/signup"
          element={
            (isAuthenticated && !isGuest)
              ? <Navigate to="/home" />
              : <SignupPage />
          }
        />
        <Route path="/login-success" element={<LoginSuccess />} />
        <Route path="/phone-verification" element={<PhoneVerificationPage />} />
        <Route path="/find-id" element={<FindIdPage />} />
        <Route path="/find-pw" element={<FindPwPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/edit-info" element={<EditInfo />} />
        <Route path="/notification-settings" element={<NotificationSettings />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/marketing-consent" element={<MarketingConsentPage />} />

        {/* 일반 페이지들 */}
        <Route path="/home" element={<MainPage />} />
        <Route path="/clubs" element={<ClubsPage />} />
        <Route path="/clubs/:id/chat" element={<ClubChatPage />} />
        <Route path="/ticket-info" element={<TicketInfoPage />} />
        <Route path="/ticket-purchase" element={<TicketPurchasePage />} />
        <Route path="/success" element={<PaymentSuccess />} />
        <Route path="/fail" element={<PaymentFail />} />
        <Route path="/announcements" element={<Announcements />} />
        <Route path="/events" element={<EventCalendar events={formattedEvents} />} />
        <Route path="/events/:id" element={<EventDetailPage events={formattedEvents} />} />
        <Route path="/admin-announcements" element={<AdminAnnouncementPage />} />
        
        {/* 기존 testclub 라우트는 /clubs로 리다이렉트 */}
        <Route path="/testclub" element={<Navigate to="/clubs" />} />

        {/* 보호된 라우트(로그인 필수) */}
        <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading} />}>
          <Route path="/likes" element={<LikesPage />} />
          <Route path="/mypage" element={<MyPage />} />
          <Route path="/mypage-v2" element={<MyPageV2 />} />
          <Route path="/customer-service" element={<CustomerService />} />
        </Route>

        {/* 루트나 기타 URL -> 로그인 or 홈 */}
        <Route
          path="/"
          element={
            (isAuthenticated && !isGuest)
              ? <Navigate to="/home" />
              : <Navigate to="/login" />
          }
        />
        <Route
          path="*"
          element={
            (isAuthenticated && !isGuest)
              ? <Navigate to="/home" />
              : <Navigate to="/login" />
          }
        />
      </Routes>

      <NavbarWrapper />
      <DeepLinkListener />
    </div>
  );
};

// ─────────────────────────────────────────────────────
// 4) 네이티브 앱 전용 컴포넌트 (푸시 알림 등록 등)
// ─────────────────────────────────────────────────────
const NativeApp = () => {
  useEffect(() => {
    registerPushNotifications();
  }, []);
  return <AppRoutes />;
};

// ─────────────────────────────────────────────────────
// 5) 웹 전용 라우트 (특정 페이지만 열 수 있게 제한, 예: 약관/개인정보)
// ─────────────────────────────────────────────────────
const WebRoutes = () => {
  return (
    <div className="web-container">
      <Routes>
        {/* 로그인된 사용자는 대시보드로 리다이렉트 */}
        <Route path="/owner/login" element={
          <>
            <OwnerAuthRedirect />
            <OwnerLoginPage />
          </>
        } />
        <Route path="/owner/register" element={
          <>
            <OwnerAuthRedirect />
            <OwnerRegisterPage />
          </>
        } />
        <Route path="/owner" element={
          <>
            <OwnerAuthRedirect />
            <AdminLandingPage />
          </>
        } />
        
        {/* 오너용 보호된 라우트 */}
        <Route element={<OwnerProtectedRoute />}>
          <Route path="/owner/dashboard" element={<OwnerDashboardPage />} />
          <Route path="/owner/edit" element={<OwnerEditInfoPage />} />
          <Route path="/owner/tickets" element={<TicketSettingsPage />} />
          <Route path="/owner/events" element={<EventManagementPage />} />
          <Route path="/owner/banners" element={<BannerManagementPage />} />
          <Route path="/owner/clubs" element={<ClubManagementPage />} />
        </Route>

        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/marketing-consent" element={<MarketingConsentPage />} />
        <Route path="/vti" element={<VTIPage />} />
        <Route path="/banner" element={<BannerAdmin />} />
        <Route path="/admin/banners" element={<BannerAdmin />} />
        <Route path="/admin/announcements" element={<AdminAnnouncementPage />} />
        {/* 그 외 경로는 랜딩 페이지로 이동 */}
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </div>
  );
};

// ─────────────────────────────────────────────────────
// 6) 최상위 App 컴포넌트
//    - 웹 vs 네이티브 플랫폼 구분 후 라우트 노출
// ─────────────────────────────────────────────────────
const App = () => {
  const forceNative = process.env.REACT_APP_FORCE_NATIVE === 'true';
  const platform = Capacitor.getPlatform();
  // 웹이면 WebRoutes, 네이티브면 NativeApp
  const useNativeApp = forceNative || platform !== 'web';

  return (
    <Provider store={store}>
      <Router>
        {useNativeApp ? <NativeApp /> : <WebRoutes />}
        <Analytics />
      </Router>
    </Provider>
  );
};

export default App;
